/**
 * Command system (tech-design §2.2). The UI never mutates state — it dispatches a
 * {@link Command}; the engine validates and applies it, returning the new state
 * plus {@link GameEvent}s (render/ui juice the sim never depends on). Invalid
 * commands are rejected with a **typed reason** — never a silent no-op.
 */
import { ONE, ZERO, add, gte, num, sub, type Num } from './num.js';
import { chanceHit, weightedIndex, type RngState } from './rng.js';
import type { Breakthrough } from '@content/index.js';
import type { Content } from './content.js';
import { checkMilestones, refreshMultipliers, type MilestoneEvent } from './derive.js';
import {
  breakthroughChance,
  breakthroughCost,
  cumulativeGeneratorCost,
  evalCurve,
  generatorMultiplier,
  isPrestigeUnlocked,
  isUnlocked,
  maxAffordable,
  parseTarget,
  prestigeGain,
} from './selectors.js';
import {
  addToSet,
  getBreakthrough,
  getGenerator,
  getPrestige,
  getResource,
  type GameState,
} from './state.js';

// ── Commands ──────────────────────────────────────────────────────────────────

export type Command =
  | { readonly type: 'click'; readonly actionId: string }
  | { readonly type: 'buyGenerator'; readonly id: string; readonly count: number | 'max' }
  | { readonly type: 'buyUpgrade'; readonly id: string }
  | { readonly type: 'attemptBreakthrough'; readonly id: string }
  | { readonly type: 'prestige'; readonly layerId: string }
  | {
      readonly type: 'buyPrestigeUpgrade';
      readonly layerId: string;
      readonly id: string;
    }
  | { readonly type: 'selectAge'; readonly id: string };

// ── Events (consumed by render/ui only) ────────────────────────────────────────

export type GameEvent =
  | { readonly type: 'clicked'; readonly actionId: string }
  | {
      readonly type: 'bought';
      readonly kind: 'generator' | 'upgrade' | 'prestigeUpgrade';
      readonly id: string;
      readonly count: number;
    }
  | { readonly type: 'breakthroughAttempt'; readonly id: string; readonly hit: boolean }
  | {
      readonly type: 'breakthroughHit';
      readonly id: string;
      readonly traits: readonly string[];
    }
  | { readonly type: 'ageCompleted'; readonly age: string }
  | { readonly type: 'ageSelected'; readonly id: string }
  | { readonly type: 'prestiged'; readonly layerId: string; readonly gained: string }
  | MilestoneEvent;

// ── Rejection reasons ───────────────────────────────────────────────────────────

export type RejectReason =
  | 'unknownEntity'
  | 'locked'
  | 'cannotAfford'
  | 'nothingToBuy'
  | 'alreadyOwned'
  | 'invalidCount'
  | 'noAgeSelected';

export interface ApplyResult {
  readonly state: GameState;
  readonly events: readonly GameEvent[];
  /** `null` on success; a typed reason when the command was rejected. */
  readonly rejected: RejectReason | null;
}

const reject = (state: GameState, reason: RejectReason): ApplyResult => ({
  state,
  events: [],
  rejected: reason,
});

// ── Resource helpers ────────────────────────────────────────────────────────────

/** Grant a resource: raises both current amount and lifetime total. */
function gainResource(state: GameState, id: string, delta: Num): GameState {
  const r = getResource(state, id);
  return {
    ...state,
    resources: {
      ...state.resources,
      [id]: { amount: add(r.amount, delta), lifetime: add(r.lifetime, delta) },
    },
  };
}

/** Spend a resource: lowers current amount (never below zero), lifetime untouched. */
function spendResource(state: GameState, id: string, delta: Num): GameState {
  const r = getResource(state, id);
  const amount = sub(r.amount, delta);
  return {
    ...state,
    resources: {
      ...state.resources,
      [id]: { amount: amount.lt(ZERO) ? ZERO : amount, lifetime: r.lifetime },
    },
  };
}

function spendCosts(
  state: GameState,
  costs: readonly { readonly resource: string; readonly amount: Num }[],
): GameState {
  return costs.reduce((s, c) => spendResource(s, c.resource, c.amount), state);
}

const canAfford = (
  state: GameState,
  costs: readonly { readonly resource: string; readonly amount: Num }[],
): boolean => costs.every((c) => gte(getResource(state, c.resource).amount, c.amount));

/** Recompute derived state after an effect/resource change, collecting events. */
function settle(state: GameState, content: Content, events: GameEvent[]): ApplyResult {
  const tracked = trackMaxAgeOrder(state, content);
  const refreshed = refreshMultipliers(tracked, content);
  const milestones = checkMilestones(refreshed, content);
  return {
    state: milestones.state,
    events: [...events, ...milestones.events],
    rejected: null,
  };
}

/** Record the highest age order ever played — a lifetime stat no reset touches
 * (gates full-reset prestige availability, GAM-11). */
function trackMaxAgeOrder(state: GameState, content: Content): GameState {
  const current = state.ages.current;
  const order = current === null ? 0 : (content.ages.get(current)?.order ?? 0);
  if (order <= state.meta.maxAgeOrderReached) return state;
  return { ...state, meta: { ...state.meta, maxAgeOrderReached: order } };
}

// ── The dispatcher ───────────────────────────────────────────────────────────────

export function apply(state: GameState, command: Command, content: Content): ApplyResult {
  switch (command.type) {
    case 'click':
      return applyClick(state, command.actionId, content);
    case 'buyGenerator':
      return applyBuyGenerator(state, command.id, command.count, content);
    case 'buyUpgrade':
      return applyBuyUpgrade(state, command.id, content);
    case 'attemptBreakthrough':
      return applyBreakthrough(state, command.id, content);
    case 'prestige':
      return applyPrestige(state, command.layerId, content);
    case 'buyPrestigeUpgrade':
      return applyBuyPrestigeUpgrade(state, command.layerId, command.id, content);
    case 'selectAge':
      return applySelectAge(state, command.id, content);
  }
}

function applyClick(state: GameState, actionId: string, content: Content): ApplyResult {
  const current = state.ages.current;
  if (current === null) return reject(state, 'noAgeSelected');
  const age = content.ages.get(current);
  if (age?.clickAction.id !== actionId) {
    return reject(state, 'unknownEntity');
  }
  let next = state;
  for (const grant of age.clickAction.grants) {
    next = gainResource(next, grant.resource, num(grant.amount));
  }
  return settle(next, content, [{ type: 'clicked', actionId }]);
}

function applyBuyGenerator(
  state: GameState,
  id: string,
  countArg: number | 'max',
  content: Content,
): ApplyResult {
  const def = content.generators.get(id);
  if (def === undefined) return reject(state, 'unknownEntity');
  if (!isUnlocked(state, content, def)) return reject(state, 'locked');

  let count: number;
  if (countArg === 'max') {
    count = maxAffordable(state, content, id);
    if (count <= 0) return reject(state, 'cannotAfford');
  } else {
    if (!Number.isInteger(countArg) || countArg <= 0)
      return reject(state, 'invalidCount');
    count = countArg;
  }

  const cost = cumulativeGeneratorCost(state, content, id, count);
  if (!gte(getResource(state, def.cost.resource).amount, cost)) {
    return reject(state, 'cannotAfford');
  }

  const owned = getGenerator(state, id).owned + count;
  let next = spendResource(state, def.cost.resource, cost);
  next = {
    ...next,
    generators: { ...next.generators, [id]: { owned, multiplier: ONE } },
  };
  next = {
    ...next,
    generators: {
      ...next.generators,
      [id]: { owned, multiplier: generatorMultiplier(next, content, id) },
    },
  };
  return settle(next, content, [{ type: 'bought', kind: 'generator', id, count }]);
}

function applyBuyUpgrade(state: GameState, id: string, content: Content): ApplyResult {
  const def = content.upgrades.get(id);
  if (def === undefined) return reject(state, 'unknownEntity');
  if (state.upgrades.includes(id)) return reject(state, 'alreadyOwned');
  if (!isUnlocked(state, content, def)) return reject(state, 'locked');

  // Upgrades are bought once; the cost curve is evaluated at index 0 (a flat price).
  const cost = { resource: def.cost.resource, amount: evalCurve(def.cost.curve, 0) };
  if (!canAfford(state, [cost])) return reject(state, 'cannotAfford');

  let next = spendResource(state, cost.resource, cost.amount);
  next = { ...next, upgrades: addToSet(next.upgrades, id) };
  return settle(next, content, [{ type: 'bought', kind: 'upgrade', id, count: 1 }]);
}

function applyBreakthrough(state: GameState, id: string, content: Content): ApplyResult {
  const def = content.breakthroughs.get(id);
  if (def === undefined) return reject(state, 'unknownEntity');
  if (!isUnlocked(state, content, def)) return reject(state, 'locked');

  const costs = breakthroughCost(state, content, id);
  if (!canAfford(state, costs)) return reject(state, 'cannotAfford');

  let next = spendCosts(state, costs);
  const prior = getBreakthrough(next, id);
  const chance = breakthroughChance(next, content, id);
  const roll = chanceHit(next.rng, chance);
  const rng: RngState = roll.rng;

  if (!roll.value) {
    // Miss: bank pity, advance the RNG.
    const inc = def.pity?.incrementPerFail ?? 0;
    next = {
      ...next,
      rng,
      breakthroughs: {
        ...next.breakthroughs,
        [id]: { attempts: prior.attempts + 1, pity: prior.pity + inc, hits: prior.hits },
      },
    };
    return settle(next, content, [{ type: 'breakthroughAttempt', id, hit: false }]);
  }

  // Hit: grant rewards, count the hit, reset pity if configured.
  const resetPity = def.pity?.resetOnHit ?? true;
  const grant = grantReward(next, def, rng);
  next = grant.state;
  next = {
    ...next,
    rng: grant.rng,
    breakthroughs: {
      ...next.breakthroughs,
      [id]: {
        attempts: prior.attempts + 1,
        pity: resetPity ? 0 : prior.pity,
        hits: prior.hits + 1,
      },
    },
  };

  const events: GameEvent[] = [
    { type: 'breakthroughAttempt', id, hit: true },
    { type: 'breakthroughHit', id, traits: grant.traits },
  ];

  // Age-boss roll: completing it completes the age (§7).
  if (def.rewards.ageComplete === true && next.ages.current !== null) {
    const age = next.ages.current;
    next = {
      ...next,
      ages: { current: age, completed: addToSet(next.ages.completed, age) },
    };
    events.push({ type: 'ageCompleted', age });
  }

  return settle(next, content, events);
}

/** Resolve a breakthrough's reward per its mode; returns granted traits + rng. */
function grantReward(
  state: GameState,
  def: Breakthrough,
  rng: RngState,
): { readonly state: GameState; readonly rng: RngState; readonly traits: string[] } {
  const rewards = def.rewards;
  const pool = rewards.pool ?? [];
  if (pool.length === 0) return { state, rng, traits: [] };

  // Exhaustible pools never re-grant a trait already owned.
  const eligible =
    rewards.exhaustible === true
      ? pool.filter((entry) => !state.traits.includes(entry.trait))
      : pool;
  if (eligible.length === 0) return { state, rng, traits: [] };

  switch (rewards.mode) {
    case 'randomFromPool': {
      const pick = weightedIndex(
        rng,
        eligible.map((e) => e.weight),
      );
      const chosen = eligible[pick.value];
      if (chosen === undefined) return { state, rng: pick.rng, traits: [] };
      return {
        state: { ...state, traits: addToSet(state.traits, chosen.trait) },
        rng: pick.rng,
        traits: [chosen.trait],
      };
    }
    case 'fixedOrder': {
      const hits = getBreakthrough(state, def.id).hits;
      const chosen = pool[hits % pool.length];
      if (chosen === undefined) return { state, rng, traits: [] };
      return {
        state: { ...state, traits: addToSet(state.traits, chosen.trait) },
        rng,
        traits: [chosen.trait],
      };
    }
    case 'chooseFrom': {
      // The player's pick arrives via UI in later work; auto-grant the count from
      // the eligible front so the engine never leaves a hit unrewarded.
      const take = rewards.chooseCount ?? 1;
      const chosenList = eligible.slice(0, take).map((e) => e.trait);
      let next = state;
      for (const trait of chosenList)
        next = { ...next, traits: addToSet(next.traits, trait) };
      return { state: next, rng, traits: chosenList };
    }
  }
}

function applyPrestige(state: GameState, layerId: string, content: Content): ApplyResult {
  const layer = content.prestige.get(layerId);
  if (layer === undefined) return reject(state, 'unknownEntity');
  if (!isPrestigeUnlocked(state, content, layerId)) return reject(state, 'locked');

  const gained = prestigeGain(state, content, layerId);
  const prior = getPrestige(state, layerId);

  let next = resetScope(state, content, layer);
  const layerState = {
    currency: add(prior.currency, gained),
    purchased: prior.purchased,
    count: prior.count + 1,
  };
  next = {
    ...next,
    // A full reset seeds a new universe: every *other* layer's currency, purchases
    // and count are wiped too — only this layer's eternal currency survives (GAM-11).
    prestige:
      layer.scope === 'everything'
        ? { [layerId]: layerState }
        : { ...next.prestige, [layerId]: layerState },
  };
  if (layer.scope === 'everything') {
    next = applyKeepsakes(next, layer);
  }
  return settle(next, content, [
    { type: 'prestiged', layerId, gained: gained.toString() },
  ]);
}

/** Owned keepsake tree upgrades (`resource.<id>.start` / `trait.<id>.start` effects)
 * grant their head-start at the birth of each new universe (GAM-11). */
function applyKeepsakes(
  state: GameState,
  layer: NonNullable<ReturnType<Content['prestige']['get']>>,
): GameState {
  let next = state;
  const purchased = getPrestige(state, layer.id).purchased;
  for (const upgrade of layer.tree) {
    if (!purchased.includes(upgrade.id)) continue;
    for (const effect of upgrade.effects) {
      const target = parseTarget(effect.target);
      if (target?.prop !== 'start' || effect.op !== 'add') continue;
      if (target.kind === 'resource') {
        const prior = getResource(next, target.id);
        const grant = num(effect.value);
        next = {
          ...next,
          resources: {
            ...next.resources,
            [target.id]: {
              amount: add(prior.amount, grant),
              lifetime: add(prior.lifetime, grant),
            },
          },
        };
      } else if (target.kind === 'trait') {
        next = { ...next, traits: addToSet(next.traits, target.id) };
      }
    }
  }
  return next;
}

/** Reset the layer's scope: the current age, or everything. A full reset honors the
 * layer's `keeps` list (`keystoneTraits`, `milestones`) and seeds a new universe:
 * ages (current and completed) reset too, ready for the first age (GAM-11). */
function resetScope(
  state: GameState,
  content: Content,
  layer: NonNullable<ReturnType<Content['prestige']['get']>>,
): GameState {
  const scope = layer.scope;
  const keepTrait = (id: string): boolean => content.traits.get(id)?.keystone === true;

  if (scope === 'everything') {
    return {
      ...state,
      resources: {},
      generators: {},
      upgrades: [],
      traits: layer.keeps.includes('keystoneTraits')
        ? state.traits.filter(keepTrait)
        : [],
      milestones: layer.keeps.includes('milestones') ? state.milestones : [],
      breakthroughs: {},
      ages: { current: null, completed: [] },
    };
  }

  // currentAge: clear only entities belonging to the age being played. Rebuild the
  // records without the age's keys (no dynamic delete).
  const ageId = state.ages.current;
  const belongsToAge = (
    id: string,
    ageOf: ReadonlyMap<string, { readonly age: string }>,
  ): boolean => ageOf.get(id)?.age === ageId;
  const resources = Object.fromEntries(
    Object.entries(state.resources).filter(
      ([id]) => !belongsToAge(id, content.resources),
    ),
  );
  const generators = Object.fromEntries(
    Object.entries(state.generators).filter(
      ([id]) => !belongsToAge(id, content.generators),
    ),
  );
  const breakthroughs = Object.fromEntries(
    Object.entries(state.breakthroughs).filter(
      ([id]) => !belongsToAge(id, content.breakthroughs),
    ),
  );
  const traits = state.traits.filter(
    (id) => content.traits.get(id)?.age !== ageId || keepTrait(id),
  );
  return { ...state, resources, generators, breakthroughs, traits };
}

/** Buy an upgrade in a prestige layer's tree, costed in the layer currency. */
function applyBuyPrestigeUpgrade(
  state: GameState,
  layerId: string,
  id: string,
  content: Content,
): ApplyResult {
  const layer = content.prestige.get(layerId);
  if (layer === undefined) return reject(state, 'unknownEntity');
  const def = layer.tree.find((u) => u.id === id);
  if (def === undefined) return reject(state, 'unknownEntity');

  const prior = getPrestige(state, layerId);
  if (prior.purchased.includes(id)) return reject(state, 'alreadyOwned');
  if (!isUnlocked(state, content, def)) return reject(state, 'locked');

  // Like age upgrades, tree upgrades are bought once at a flat price — but the
  // price is denominated in the layer currency, not a resource.
  const price = def.cost === undefined ? ZERO : evalCurve(def.cost.curve, 0);
  if (!gte(prior.currency, price)) return reject(state, 'cannotAfford');

  const next: GameState = {
    ...state,
    prestige: {
      ...state.prestige,
      [layerId]: {
        currency: sub(prior.currency, price),
        purchased: [...prior.purchased, id],
        count: prior.count,
      },
    },
  };
  return settle(next, content, [
    { type: 'bought', kind: 'prestigeUpgrade', id, count: 1 },
  ]);
}

function applySelectAge(state: GameState, id: string, content: Content): ApplyResult {
  const age = content.ages.get(id);
  if (age === undefined) return reject(state, 'unknownEntity');
  if (!isUnlocked(state, content, age)) return reject(state, 'locked');
  const next: GameState = {
    ...state,
    ages: { current: id, completed: state.ages.completed },
  };
  return settle(next, content, [{ type: 'ageSelected', id }]);
}
